import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Pagination } from './ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { getPaginationConfig } from '../config/uiConfig';
import { 
  Warehouse, 
  Loader2, 
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { rmDashboardApiService, RMDashboardData, RMAgedInventoryDto } from '../services/rmDashboardApi';
import { getRMDashboardConfig } from '../config/uiConfig';
import { inventoryReportApiService, InventoryAgingAnalysisDto } from '../services/inventoryReportApi';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LabelList
} from 'recharts';

// Pie chart palette (requested): #003D64, #0568BF, #FD6500, #F5EA54, #FFFFFF
const COLORS = ['#003D64', '#0568BF', '#FD6500', '#F5EA54', '#FFFFFF'];
const RADIAN = Math.PI / 180;

const renderBranchLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  branchCode,
  quantity
}: any) => {
  const radius = outerRadius + 24;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#0f172a"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={16}
      fontWeight={600}
    >
      {`${branchCode}: ${quantity ? quantity.toFixed(2) : '0.00'} T`}
    </text>
  );
};

const RMDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<RMDashboardData | null>(null);
  const [selectedBranchId, setSelectedBranchId] = useState<number | undefined>(undefined);
  // Store original branch inventory data (all branches) for filtering purposes
  const [allBranchesData, setAllBranchesData] = useState<RMDashboardData['branchInventory']>([]);
  // Use ref to track selected branch to prevent accidental resets
  const selectedBranchIdRef = useRef<number | undefined>(undefined);
  
  // Pagination state for aged inventory
  const paginationConfig = getPaginationConfig();
  const rmDashboardConfig = getRMDashboardConfig();
  const [agedInventoryCurrentPage, setAgedInventoryCurrentPage] = useState(1);
  const [agedInventoryItemsPerPage, setAgedInventoryItemsPerPage] = useState(rmDashboardConfig.agedInventoryItemsPerPage);
  
  // Aging analysis state
  const [agingAnalysis, setAgingAnalysis] = useState<InventoryAgingAnalysisDto | null>(null);
  const [loadingAgingAnalysis, setLoadingAgingAnalysis] = useState(false);
  const [agedInventory, setAgedInventory] = useState<RMAgedInventoryDto[]>([]);

  // Load all branches data on initial mount
  useEffect(() => {
    const loadAllBranchesData = async () => {
      try {
        const response = await rmDashboardApiService.getRMDashboardData(undefined);
        if (response.success && response.data?.branchInventory) {
          setAllBranchesData(response.data.branchInventory);
        }
      } catch (error) {
        // console.error('Error loading all branches data:', error);
      }
    };
    loadAllBranchesData();
  }, []);

  // Update ref when selectedBranchId changes
  useEffect(() => {
    selectedBranchIdRef.current = selectedBranchId;
  }, [selectedBranchId]);

  useEffect(() => {
    loadDashboardData();
    loadAgingAnalysis();
    loadAgedInventory();
  }, [selectedBranchId]);

  // Load inventory aging analysis
  const loadAgingAnalysis = async () => {
    const currentBranchId = selectedBranchIdRef.current ?? selectedBranchId;
    // Pass undefined when no branch is selected to get data for all branches

    try {
      setLoadingAgingAnalysis(true);
      const response = await inventoryReportApiService.getInventoryAgingAnalysis(currentBranchId);
      
      if (response.success && response.data) {
        setAgingAnalysis(response.data);
      } else {
        setAgingAnalysis(null);
      }
    } catch (error) {
      // console.error('Error loading aging analysis:', error);
      setAgingAnalysis(null);
    } finally {
      setLoadingAgingAnalysis(false);
    }
  };

  // Load aged inventory (older than 6 months) via dedicated endpoint
  const loadAgedInventory = async () => {
    const currentBranchId = selectedBranchIdRef.current ?? selectedBranchId;
    try {
      const response = await rmDashboardApiService.getRMAgedInventory(currentBranchId);
      if (response.success && response.data) {
        setAgedInventory(response.data);
      } else {
        setAgedInventory([]);
      }
    } catch (error) {
      // console.error('Error loading aged inventory:', error);
      setAgedInventory([]);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      // Use ref value to ensure we're using the most current selectedBranchId
      const currentBranchId = selectedBranchIdRef.current ?? selectedBranchId;
      // Fetch data for selected branch or all branches
      const response = await rmDashboardApiService.getRMDashboardData(currentBranchId);
      if (response.success && response.data) {
        setDashboardData(response.data);
        // Always update allBranchesData if we're loading all branches (no filter)
        if (!currentBranchId && response.data.branchInventory) {
          setAllBranchesData(response.data.branchInventory);
        }
      } else {
        toast.error(response.message || 'Failed to load RM dashboard data');
      }
    } catch (error) {
      // console.error('Error loading RM dashboard data:', error);
      toast.error('Failed to load RM dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleBranchClick = (data: any) => {
    // Recharts passes the entire data object when clicking on a pie segment
    // The data object contains branchCode, branchId, name, quantity, value
    // Use branchCode to find and filter by branch
    const clickedBranchCode = data?.branchCode;
    
    if (clickedBranchCode) {
      // First try to find in allBranchesData (all branches) to ensure we can always find the branch
      // This works even when filtered data only shows one branch
      const clickedBranch = allBranchesData.find(
        branch => branch.branchCode === clickedBranchCode
      );
      
      if (clickedBranch && clickedBranch.branchId) {
        // Use the branchId to filter - only update if it's different
        if (selectedBranchIdRef.current !== clickedBranch.branchId) {
          selectedBranchIdRef.current = clickedBranch.branchId;
      setSelectedBranchId(clickedBranch.branchId);
        }
        return;
      }
      
      // Fallback: try current dashboardData.branchInventory
      const currentBranch = dashboardData?.branchInventory?.find(
        branch => branch.branchCode === clickedBranchCode
      );
      
      if (currentBranch && currentBranch.branchId) {
        if (selectedBranchIdRef.current !== currentBranch.branchId) {
          selectedBranchIdRef.current = currentBranch.branchId;
          setSelectedBranchId(currentBranch.branchId);
        }
        return;
      }
      
      // Last fallback: find in branchChartData using branchCode
      const chartDataItem = branchChartData.find(
        item => item.branchCode === clickedBranchCode
      );
      
      if (chartDataItem && chartDataItem.branchId) {
        if (selectedBranchIdRef.current !== chartDataItem.branchId) {
          selectedBranchIdRef.current = chartDataItem.branchId;
          setSelectedBranchId(chartDataItem.branchId);
        }
      }
    } else if (data?.branchId) {
      // If branchId is directly available, use it
      if (selectedBranchIdRef.current !== data.branchId) {
        selectedBranchIdRef.current = data.branchId;
        setSelectedBranchId(data.branchId);
      }
    }
  };

  const handleClearFilter = () => {
    selectedBranchIdRef.current = undefined;
    setSelectedBranchId(undefined);
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return '0.00';
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Prepare branch inventory data for pie chart
  const branchChartData = useMemo(() => {
    if (!dashboardData?.branchInventory) return [];
    return dashboardData.branchInventory.map(item => ({
      name: item.branchName || item.branchCode || 'Unknown',
      branchCode: item.branchCode || 'N/A',
      branchId: item.branchId,
      quantity: item.totalQuantity ?? 0,
      value: item.totalValue ?? 0,
    }));
  }, [dashboardData]);

  // Prepare category inventory data for bar chart (top 10)
  const categoryChartData = useMemo(() => {
    if (!dashboardData?.categoryInventory) return [];
    return dashboardData.categoryInventory
      .map(item => ({
        name: item.categoryName || 'Unknown',
        quantity: item.quantity ?? 0,
        totalQuantity: item.totalQuantity ?? item.quantity ?? 0,
        value: item.totalValue ?? 0,
      }))
      .sort((a, b) => b.totalQuantity - a.totalQuantity)
      .slice(0, 10);
  }, [dashboardData]);

  // Prepare supplier inventory data for table (top 10)
  const supplierTableData = useMemo(() => {
    if (!dashboardData?.supplierInventory) return [];
    return dashboardData.supplierInventory
      .map(item => ({
        name: item.supplierName || 'Unknown',
        quantity: item.quantity ?? 0,
        totalQuantity: item.totalQuantity ?? item.quantity ?? 0,
        value: item.value ?? 0,
        totalValue: item.totalValue ?? item.value ?? 0,
      }))
      .sort((a, b) => b.totalQuantity - a.totalQuantity)
      .slice(0, 10);
  }, [dashboardData]);

  type AgedInventoryPivotRow = {
    itemName: string;
    byBranchQty: Record<string, number>;
    totalQty: number;
    totalVal: number;
  };

  const distinctAgedBranchCodes = useMemo(() => {
    if (!agedInventory || agedInventory.length === 0) return [];
    const minQuantity = getRMDashboardConfig().agedInventoryMinQuantity;

    // Filter entries by minimum quantity (same behavior as before)
    const filtered = agedInventory.filter((i) => (i.quantity ?? 0) >= minQuantity);

    const codes = new Set<string>();
    for (const i of filtered) {
      const code = String((i as any).branchCode ?? (i as any).branch_code ?? '').trim();
      if (code) codes.add(code);
    }
    return Array.from(codes).sort((a, b) => a.localeCompare(b));
  }, [agedInventory]);

  // Aged inventory pivot rows: per item -> qty per branch, plus totals
  const sortedAgedInventory = useMemo<AgedInventoryPivotRow[]>(() => {
    if (!agedInventory || agedInventory.length === 0) return [];
    const minQuantity = getRMDashboardConfig().agedInventoryMinQuantity;
    const filtered = agedInventory.filter((i) => (i.quantity ?? 0) >= minQuantity);

    const byItem = new Map<
      string,
      { byBranchQty: Record<string, number>; totalQty: number; totalVal: number }
    >();

    for (const i of filtered) {
      const itemName = i.itemName || 'Unknown';
      const code = String((i as any).branchCode ?? (i as any).branch_code ?? '').trim() || 'NA';
      const qty = Number(i.quantity ?? 0) || 0;
      const val = Number(i.value ?? 0) || 0;

      if (!byItem.has(itemName)) {
        byItem.set(itemName, { byBranchQty: {}, totalQty: 0, totalVal: 0 });
      }
      const row = byItem.get(itemName)!;
      row.byBranchQty[code] = (row.byBranchQty[code] ?? 0) + qty;
      row.totalQty += qty;
      row.totalVal += val;
    }

    return Array.from(byItem.entries())
      .map(([itemName, r]) => ({ itemName, byBranchQty: r.byBranchQty, totalQty: r.totalQty, totalVal: r.totalVal }))
      .sort((a, b) => b.totalQty - a.totalQty);
  }, [agedInventory]);

  const agedInventoryTotals = useMemo(() => {
    return {
      totalQty: sortedAgedInventory.reduce((s, r) => s + (r.totalQty || 0), 0),
      totalVal: sortedAgedInventory.reduce((s, r) => s + (r.totalVal || 0), 0),
    };
  }, [sortedAgedInventory]);

  // When branch filter changes, reset aged inventory pagination
  useEffect(() => {
    setAgedInventoryCurrentPage(1);
  }, [selectedBranchId]);

  // Prepare items with quantity > 15T, grouped by item name
  const itemsAbove15T = useMemo(() => {
    if (!dashboardData?.topItemsByQuantity) return [];
    
    // Filter items where totalQuantity > 15 and group by item name
    return dashboardData.topItemsByQuantity
      .filter(item => (item.totalQuantity ?? 0) > 15)
      .map(item => ({
        itemName: item.itemName || 'Unknown',
        quantity: item.totalQuantity ?? 0,
        value: item.totalValue ?? 0
      }))
      .sort((a, b) => b.quantity - a.quantity);
  }, [dashboardData]);

  // Pagination for aged inventory
  const agedInventoryTotalPages = Math.ceil(sortedAgedInventory.length / agedInventoryItemsPerPage);
  const agedInventoryStartIndex = (agedInventoryCurrentPage - 1) * agedInventoryItemsPerPage;
  const agedInventoryEndIndex = agedInventoryStartIndex + agedInventoryItemsPerPage;
  const paginatedAgedInventory = sortedAgedInventory.slice(agedInventoryStartIndex, agedInventoryEndIndex);

  const handleAgedInventoryPageChange = (page: number) => {
    setAgedInventoryCurrentPage(page);
  };

  const handleAgedInventoryItemsPerPageChange = (value: string) => {
    setAgedInventoryItemsPerPage(Number(value));
    setAgedInventoryCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleAgedInventoryExport = () => {
    if (!sortedAgedInventory.length) {
      toast.error('No data to export');
      return;
    }

    try {
      const escapeCsv = (value: string) => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const headers = ['Item Name', ...distinctAgedBranchCodes, 'Total Qty', 'Total Value'];

      const csvLines = [
        headers.join(','),
        ...sortedAgedInventory.map((row) => {
          const cells = [
            escapeCsv(row.itemName),
            ...distinctAgedBranchCodes.map((code) => (row.byBranchQty[code] ?? 0).toFixed(2)),
            row.totalQty.toFixed(2),
            row.totalVal.toFixed(2),
          ];
          return cells.join(',');
        }),
      ];

      const csvContent = csvLines.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const branchSuffix = selectedBranchId ? `_Branch-${selectedBranchId}` : '';
      const filename = `Aged_Inventory_Report${branchSuffix}_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${sortedAgedInventory.length} records to CSV`);
    } catch (error) {
      // console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  // Custom tooltip for pie chart
  const CustomPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{data.name}</p>
          <p className="text-sm text-gray-600">Quantity: {formatNumber(data.quantity)}</p>
          <p className="text-sm text-gray-600">Value: {formatCurrency(data.value)}</p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for bar charts
  const CustomBarTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const quantity = data.totalQuantity ?? data.quantity ?? 0;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{data.name}</p>
          <p className="text-sm text-gray-600">Quantity: {formatNumber(quantity)}</p>
          <p className="text-sm text-gray-600">Value: {formatCurrency(data.value)}</p>
        </div>
      );
    }
    return null;
  };

  const handleItemNameClick = (itemName: string, heatNo?: string) => {
    const params = new URLSearchParams();
    params.append('item', itemName);
    if (heatNo) {
      params.append('heatNo', heatNo);
    }
    window.open(`/reports/rm-inventory?${params.toString()}`, '_blank');
  };

  const handleGrnNumberClick = (grnNumber: string) => {
    // Open inventory report with GRN number as search term
    // The inventory report searches across GRN number field
    window.open(`/reports/rm-inventory?grnNumber=${encodeURIComponent(grnNumber)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-3 text-gray-600">Loading RM dashboard data...</span>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No data available</p>
            <Button onClick={handleRefresh} variant="outline" className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <Warehouse className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Raw Material Inventory Dashboard</CardTitle>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={loading}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Branch-wise Inventory - Pie Chart */}
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-semibold text-gray-800">
                  Total Quantity by Branch
                </CardTitle>
                {selectedBranchId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearFilter}
                    className="text-xs"
                  >
                    Clear Filter
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {branchChartData.length > 0 ? (
                <div className="flex justify-center items-center">
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={branchChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderBranchLabel}
                        outerRadius={140}
                        fill="#8884d8"
                        dataKey="quantity"
                        onClick={handleBranchClick}
                        style={{ cursor: 'pointer' }}
                      >
                      {branchChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Category-wise Inventory - Bar Chart */}
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-800">
                Total Quantity by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              {categoryChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart 
                    data={categoryChartData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      width={90}
                    />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Bar dataKey="totalQuantity" fill="#3b82f6">
                      <LabelList 
                        dataKey="totalQuantity" 
                        position="right" 
                        formatter={(value: number) => formatNumber(value)}
                        style={{ fill: '#374151', fontSize: '12px', fontWeight: 500 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Inventory Aging Analysis Table */}
        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <CardTitle className="text-xl">Inventory Aging Analysis</CardTitle>
            <p className="text-sm text-gray-600 mt-1">Stock and value breakdown by age categories</p>
          </CardHeader>
          <CardContent className="p-6">
            {loadingAgingAnalysis ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600 mr-3" />
                <span className="text-gray-600">Loading aging analysis...</span>
              </div>
            ) : agingAnalysis ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-primary-50">
                      <TableHead className="font-semibold"></TableHead>
                      <TableHead className="font-semibold text-center">0-30 Days</TableHead>
                      <TableHead className="font-semibold text-center">31-60 Days</TableHead>
                      <TableHead className="font-semibold text-center">61-90 Days</TableHead>
                      <TableHead className="font-semibold text-center">91-120 Days</TableHead>
                      <TableHead className="font-semibold text-center">&gt;120 Days</TableHead>
                      <TableHead className="font-semibold text-center">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Stock (Tone) Row */}
                    <TableRow>
                      <TableCell className="font-semibold">Stock (Tone)</TableCell>
                      <TableCell className="text-center">{formatNumber(agingAnalysis.stock0To30Days)}</TableCell>
                      <TableCell className="text-center">{formatNumber(agingAnalysis.stock31To60Days)}</TableCell>
                      <TableCell className="text-center">{formatNumber(agingAnalysis.stock61To90Days)}</TableCell>
                      <TableCell className="text-center">{formatNumber(agingAnalysis.stock91To120Days)}</TableCell>
                      <TableCell className="text-center">{formatNumber(agingAnalysis.stockOver120Days)}</TableCell>
                      <TableCell className="text-center font-semibold">{formatNumber(agingAnalysis.totalStock)}</TableCell>
                    </TableRow>
                    {/* Value in Lacs Row */}
                    <TableRow>
                      <TableCell className="font-semibold">Value</TableCell>
                      <TableCell className="text-center">{formatCurrency(agingAnalysis.value0To30Days)}</TableCell>
                      <TableCell className="text-center">{formatCurrency(agingAnalysis.value31To60Days)}</TableCell>
                      <TableCell className="text-center">{formatCurrency(agingAnalysis.value61To90Days)}</TableCell>
                      <TableCell className="text-center">{formatCurrency(agingAnalysis.value91To120Days)}</TableCell>
                      <TableCell className="text-center">{formatCurrency(agingAnalysis.valueOver120Days)}</TableCell>
                      <TableCell className="text-center font-semibold">{formatCurrency(agingAnalysis.totalValue)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <Warehouse className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">No aging analysis data available</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top 10 Tables Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Supplier-wise Inventory - Table */}
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-800">
                Total 10 Suppliers by Quantity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {supplierTableData.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Supplier Name</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {supplierTableData.map((supplier, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{supplier.name}</TableCell>
                          <TableCell className="text-right">{formatNumber(supplier.totalQuantity)} T</TableCell>
                          <TableCell className="text-right">{formatCurrency(supplier.totalValue)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top 10 Items by Quantity */}
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-800">
                Top 10 Items by Quantity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData.topItemsByQuantity && dashboardData.topItemsByQuantity.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-semibold">Item Name</TableHead>
                        <TableHead className="font-semibold text-right">Quantity</TableHead>
                        <TableHead className="font-semibold text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData.topItemsByQuantity.map((item, index) => (
                        <TableRow key={index} className="hover:bg-gray-50">
                          <TableCell className="font-medium">
                            <button
                              onClick={() => handleItemNameClick(item.itemName, '')}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {item.itemName}
                            </button>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(item.totalQuantity)} T
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(item.totalValue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-gray-500">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Aged Inventory and Items > 15T Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Aged Inventory Table */}
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-semibold text-gray-800">
                  Aged Inventory (Older than 6 months)
                </CardTitle>
                {sortedAgedInventory && sortedAgedInventory.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAgedInventoryExport}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export CSV
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {sortedAgedInventory && sortedAgedInventory.length > 0 ? (
                <>
                  {/* Total Summary */}
                  <div className="mb-6 p-4 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-lg border border-primary-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-medium text-gray-600 mb-1">Total Quantity</div>
                        <div className="text-2xl font-bold text-gray-900">
                          {formatNumber(
                            agedInventoryTotals.totalQty
                          )} T
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-600 mb-1">Total Value</div>
                        <div className="text-2xl font-bold text-primary-600">
                          {formatCurrency(
                            agedInventoryTotals.totalVal
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Table Controls */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm text-gray-600">
                      Showing {sortedAgedInventory.length > 0 ? agedInventoryStartIndex + 1 : 0}-{Math.min(agedInventoryEndIndex, sortedAgedInventory.length)} of {sortedAgedInventory.length} {sortedAgedInventory.length === 1 ? 'record' : 'records'}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Items per page:</span>
                      <Select value={agedInventoryItemsPerPage.toString()} onValueChange={handleAgedInventoryItemsPerPageChange}>
                        <SelectTrigger className="w-20 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {paginationConfig.itemsPerPageOptions.map((option) => (
                            <SelectItem key={option} value={option.toString()}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-semibold">Item Name</TableHead>
                          {distinctAgedBranchCodes.map((code) => (
                            <TableHead key={code} className="font-semibold text-right">
                              {code}
                            </TableHead>
                          ))}
                          <TableHead className="font-semibold text-right">Total Qty</TableHead>
                          <TableHead className="font-semibold text-right">Total Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedAgedInventory.map((item, index) => (
                          <TableRow key={index} className="hover:bg-gray-50">
                            <TableCell className="font-medium">
                              <button
                                onClick={() => handleItemNameClick(item.itemName)}
                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                              >
                                {item.itemName}
                              </button>
                            </TableCell>
                            {distinctAgedBranchCodes.map((code) => (
                              <TableCell key={code} className="text-right">
                                {formatNumber(item.byBranchQty[code] ?? 0)}
                              </TableCell>
                            ))}
                            <TableCell className="text-right font-semibold">{formatNumber(item.totalQty)}</TableCell>
                            <TableCell className="text-right font-semibold">{formatCurrency(item.totalVal)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {agedInventoryTotalPages > 1 && (
                    <div className="mt-4 flex justify-center">
                      <Pagination
                        currentPage={agedInventoryCurrentPage}
                        totalPages={agedInventoryTotalPages}
                        onPageChange={handleAgedInventoryPageChange}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-32 text-gray-500">
                  No aged inventory found
                </div>
              )}
            </CardContent>
          </Card>

          {/* Items with Quantity > 15T */}
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-800">
                Items with Quantity &gt; 15T
              </CardTitle>
            </CardHeader>
            <CardContent>
              {itemsAbove15T && itemsAbove15T.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-semibold">Item Name</TableHead>
                        <TableHead className="font-semibold text-right">Quantity</TableHead>
                        <TableHead className="font-semibold text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itemsAbove15T.map((item, index) => (
                        <TableRow key={index} className="hover:bg-gray-50">
                          <TableCell className="font-medium">
                            <button
                              onClick={() => handleItemNameClick(item.itemName)}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {item.itemName}
                            </button>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(item.quantity)} T
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(item.value)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-gray-500">
                  No items found with quantity &gt; 15T
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RMDashboard;

